import * as React from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type {
  BeneficialOwnershipSpecificMetadata,
  CustomCheckboxField,
  CustomField,
  CustomHyperlinkField,
  CustomRadioField,
  CustomSection,
  CustomSelectField,
  CustomTextSingleLineField,
  DocumentUpload,
  IANATimezones,
  QuestionnaireResponse,
  QuestionnaireResultResponse,
  SmartRule,
  TemplateResponse,
  TemplateResponseCore
} from '@gcv/shared';
import { CustomFieldDataType, CustomFieldType, SmartRuleAction, SmartRuleOperator } from '@gcv/shared';
import { Box, debounce, useMediaQuery, useTheme } from '@mui/material';

import { ReactComponent as DeleteIcon } from 'assets/images/ic-trash.svg';
import {
  Button,
  ContainerItem,
  Error,
  Header,
  Input,
  InputCheckBox,
  InputCurrency,
  InputDate,
  InputEmail,
  InputLabel,
  InputPhone,
  InputRadio,
  InputSelect,
  InputText,
  InputUrl,
  LabelValue,
  Text,
  Tooltip
} from 'ui';
import { OverlaySpinner } from 'ui/molecules';
import palette from 'ui/theme/palette';
import { errorMessages } from 'util/forms.util';
import { interpolateValuesInText } from '../CustomForm/interpolateValuesInText';
import { PreviewCustomFormDisplayContentType } from '../CustomForm/preview-custom-form-display-content-type';
import { ConfirmSectionDeleteModal } from './confirm-section-delete-modal';
import { CustomFileUploader } from './custom-file-uploader';
import { CustomFormFileDownload } from './custom-form-file-download';
import { CustomFormGeneratorPreview } from './custom-form-generator-preview';
import { CustomHyperlink } from './custom-hyperlink';
import { InputNumberClone } from './input-number-clone';

interface Props extends Record<string, unknown> {
  form: UseFormReturn<FieldValues>;
  requirement: TemplateResponse | QuestionnaireResponse;
  editMode: boolean;
  onFileUpload: (
    requirementId: string,
    fieldId: string,
    files: File[],
    requirementResultId?: string
  ) => Promise<DocumentUpload | undefined> | undefined;
  onFileDelete: (
    requirementId: string,
    fieldId: string,
    file: DocumentUpload,
    requirementResultId?: string
  ) => void;
  documentMap: { [id: string]: DocumentUpload };
  isLoading: boolean;
  crbTimezone: IANATimezones;
  fieldWidth?: string | { xs?: string; sm?: string; md?: string; lg?: string };
  refreshFileComponent?: boolean;
  disableFileDelete?: boolean;
  interpolationValues?: BeneficialOwnershipSpecificMetadata;
  results?: QuestionnaireResultResponse;
  onBlur?: (fieldId: string, sectionId?: string, requirementResultId?: string) => void;
  addExistingFilesToMap?: (fieldId: string, docIds: string[]) => void;
  addSection?: (requirementResultId: string) => void;
  deleteSection?: (requirementResultId: string) => void;
}

export const shouldDisplayFieldWithSmartRule = (
  smartRule: SmartRule | undefined,
  formValues: FieldValues,
  template?: TemplateResponseCore,
  /** result_id where the smart rule we are trying to compute lives. If a smart rule points to or targets a repeated section field,
   * we have to assume that the smart rule lives on the same repeated section or else it doesnt make sense.
   * Therefore, when we search the form for a smart rule target that lives on a repeated section,
   * we have to look at the fieldId_resultId composite key value where the result id is the same for the smart rule and its target */
  resultId?: string
) => {
  if (smartRule) {
    let result = true;

    smartRule.conditions.forEach((c) => {
      let targetFieldId = c.targetFieldId;
      //find the requirement where the target field id lives. if it lives on a repeated section,
      //we have to check the target field value on the result of the same field we are trying to hide or show
      const requirementOfTargetedField = Object.values(template?.custom_requirements ?? []).find(
        (requirement) => {
          return requirement.custom_section.fields.some((field) => field.id === c.targetFieldId);
        }
      );

      if (requirementOfTargetedField?.custom_section?.repeatable) {
        targetFieldId = `${c.targetFieldId}_${resultId}`;
      }

      switch (c.operator) {
        case SmartRuleOperator.IsEmpty:
          result =
            result &&
            (!formValues[targetFieldId] ||
              formValues[targetFieldId] === '' ||
              formValues[targetFieldId] === '--');
          break;

        case SmartRuleOperator.IsEqualTo:
          result =
            result &&
            (formValues[targetFieldId] === c.targetValue?.trim() ||
              (typeof formValues[targetFieldId] === 'string' &&
                formValues[targetFieldId].includes(';') &&
                formValues[targetFieldId].split(';').find((i: string) => i === c.targetValue?.trim())));
          break;

        case SmartRuleOperator.IsFilled:
          result =
            result &&
            formValues[targetFieldId] &&
            formValues[targetFieldId] !== '' &&
            formValues[targetFieldId] !== '--';
          break;

        case SmartRuleOperator.IsNotEqualTo:
          result =
            result &&
            (formValues[targetFieldId] !== c.targetValue?.trim() ||
              (typeof formValues[targetFieldId] === 'string' &&
                !formValues[targetFieldId].includes(c.targetValue?.trim())));
          break;

        default:
          break;
      }
    });

    if (smartRule.action === SmartRuleAction.Show) {
      return result;
    } else if (smartRule.action === SmartRuleAction.Hide) {
      return !result;
    }
  }

  return true;
};

export const CustomFormGenerator: React.FC<Props> = (props) => {
  // Updates on change of radio/select fields as its used only for smart-rule
  const [formValues, setFormValues] = React.useState(props.form.getValues());
  const [animate, setAnimation] = React.useState(false);
  const [deletingASection, setDeletingASection] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [deletingSectionId, setDeletingSectionId] = React.useState<string>('');
  const [deletingSectionLabel, setDeletingSectionLabel] = React.useState<string>('');
  const [confirmSectionDeleteModalOpen, setConfirmSectionDeleteModalOpen] = React.useState(false);
  const [newSection, setNewSection] = React.useState<{
    order: number | undefined;
    requirementId: string;
  } | null>(null);
  const [addingSection, setAddingSection] = React.useState(false);

  const debouncedSaveFormValues = React.useCallback(
    debounce((data: FieldValues) => {
      setFormValues(data);
    }, 150),
    []
  );

  const resetFieldValue = (fieldId: string) => {
    if (props.form.getValues()[fieldId]) {
      props.form.setValue(fieldId, undefined);
    }
  };

  const onValueChange = (fieldId: string, sectionId?: string, requirementResultId?: string) => {
    if (props.onBlur) {
      props.onBlur(fieldId, sectionId, requirementResultId);
    }
    debouncedSaveFormValues(props.form.getValues());
    if (!animate) {
      setAnimation(true);
    }
  };

  const onChange = React.useCallback(
    debounce((fieldId: string, sectionId?: string, requirementResultId?: string) => {
      if (props.onBlur) {
        props.onBlur(fieldId, sectionId, requirementResultId);
      }
    }, 2000),
    []
  );

  const NumberInputWrapper: React.FC<any> = (wrapperProps) => {
    return <Input {...wrapperProps} disableAutoWidth fullWidth />;
  };

  const getField = (
    field: CustomField,
    requirementId: string,
    requirementResultId?: string,
    repeatable?: boolean
  ) => {
    const name = field.id + (repeatable ? `_${requirementResultId}` : '');
    const existingValue = props.form.getValues()[name];
    switch (field.type) {
      case CustomFieldType.TextSingleLine:
        switch ((field as CustomTextSingleLineField).dataType) {
          case CustomFieldDataType.Currency:
            return (
              <InputCurrency
                {...props.form}
                label={field.label}
                name={name}
                viewOnly={!props.editMode}
                rules={{ required: { message: 'is required', value: field.required || false } }}
                dataCy={name}
                onChange={() => onChange(field.id, requirementId, requirementResultId)}
                onBlur={
                  props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
                }
                {...props.form}
              />
            );

          case CustomFieldDataType.Email:
            if (!existingValue || existingValue === '--') props.form.setValue(name, '');
            return (
              <InputEmail
                label={field.label}
                name={name}
                viewOnly={!props.editMode}
                rules={{ required: { message: 'is required', value: field.required || false } }}
                dataCy={name}
                onChange={() => onChange(field.id, requirementId, requirementResultId)}
                onBlur={
                  props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
                }
                disableAutoWidth
                darkLabel
                {...props.form}
              />
            );

          case CustomFieldDataType.Number:
            // TODO: remove this once we implement a ssn field type
            if (!props.editMode && field.label === 'US Person - SSN') {
              return (
                <LabelValue
                  label={field.label}
                  value={props.form.getValues(name) || '--'}
                  mask
                  labelPrimary
                />
              );
            }
            return (
              <InputNumberClone
                unformatted
                label={field.label}
                name={name}
                viewOnly={!props.editMode}
                rules={{
                  required: { message: 'is required', value: field.required || false }
                }}
                dataCy={name}
                onChange={() => onChange(field.id, requirementId, requirementResultId)}
                onBlur={
                  props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
                }
                customInput={NumberInputWrapper}
                {...props.form}
              />
            );

          case CustomFieldDataType.Phone:
            return (
              <InputPhone
                label={field.label}
                name={name}
                viewOnly={!props.editMode}
                rules={{ required: { message: 'is required', value: field.required || false } }}
                dataCy={name}
                onBlur={
                  props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
                }
                onChange={() => onChange(field.id, requirementId, requirementResultId)}
                darkLabel
                {...props.form}
              />
            );

          case CustomFieldDataType.Text:
            if (!existingValue || existingValue === '--') props.form.setValue(name, '');
            // TODO: remove this once we implement a ssn field type
            if (!props.editMode && field.label === 'Non US-Person - SSN, ITIN, Passport, etc.') {
              return (
                <LabelValue
                  label={field.label}
                  value={props.form.getValues(name) || '--'}
                  mask
                  labelPrimary
                />
              );
            }
            return (
              <InputText
                label={field.label}
                name={name}
                viewOnly={!props.editMode}
                rules={{ required: { message: 'is required', value: field.required || false } }}
                dataCy={name}
                onBlur={
                  props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
                }
                onChange={() => onChange(field.id, requirementId, requirementResultId)}
                disableAutoWidth
                darkLabel
                {...props.form}
              />
            );

          case CustomFieldDataType.Url:
            if (!existingValue || existingValue === '--') props.form.setValue(name, '');
            return (
              <InputUrl
                label={field.label}
                name={name}
                viewOnly={!props.editMode}
                rules={{ required: { message: 'is required', value: field.required || false } }}
                dataCy={name}
                onBlur={
                  props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
                }
                onChange={() => onChange(field.id, requirementId, requirementResultId)}
                darkLabel
                {...props.form}
              />
            );

          default:
            break;
        }
        break;

      case CustomFieldType.TextMultiLine:
        if (!existingValue || existingValue === '--') props.form.setValue(name, '');
        return (
          <InputText
            label={field.label}
            name={name}
            multiline
            minRows={3}
            viewOnly={!props.editMode}
            rules={{ required: { message: 'is required', value: field.required || false } }}
            dataCy={name}
            onBlur={
              props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
            }
            onChange={() => onChange(field.id, requirementId, requirementResultId)}
            disableAutoWidth
            darkLabel
            {...props.form}
          />
        );

      case CustomFieldType.Select:
        return (
          <InputSelect
            listItemsWidth="auto"
            label={field.label}
            name={name}
            options={(field as CustomSelectField).options.split(';').map((option) => {
              return {
                label: option.trim(),
                value: option.trim()
              };
            })}
            viewOnly={!props.editMode}
            rules={{ required: { message: 'is required', value: field.required || false } }}
            dataCy={name}
            onBlur={
              props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
            }
            additionalOnChange={() => onValueChange(field.id, requirementId, requirementResultId)}
            {...props.form}
          />
        );

      case CustomFieldType.Radio:
        return (
          <InputRadio
            label={field.label}
            name={name}
            options={(field as CustomRadioField).options.split(';').map((option) => {
              return {
                label: option.trim(),
                value: option.trim()
              };
            })}
            justifyContent="column"
            viewOnly={!props.editMode}
            rules={{ required: { message: 'is required', value: field.required || false } }}
            dataCy={name}
            onBlur={
              props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
            }
            onChange={
              props.onBlur
                ? () => props.onBlur!(field.id, requirementId, requirementResultId)
                : () => onValueChange(field.id, requirementId, requirementResultId)
            }
            {...props.form}
          />
        );

      case CustomFieldType.File:
        return (
          <CustomFileUploader
            name={name}
            label={field.label}
            required={field.required}
            attachments={field.attachments}
            form={props.form}
            editMode={props.editMode}
            documentMap={props.documentMap}
            onFileUpload={props.onFileUpload}
            onFileDelete={props.onFileDelete}
            requirementId={requirementId}
            addExistingFilesToMap={props.addExistingFilesToMap}
            refreshFileComponent={props.refreshFileComponent}
            disableFileDelete={props.disableFileDelete}
            orgId={props.requirement.bank_id}
            requirementResultId={requirementResultId}
          />
        );

      case CustomFieldType.Checkbox:
        return (
          <>
            <InputLabel label={field.label} name={name} required={field.required} darkLabel />
            <Box
              sx={{ display: 'inline-flex', flexDirection: 'column', paddingTop: '5px', flexWrap: 'wrap' }}
            >
              {(field as CustomCheckboxField).options.split(';').map((option, index) => {
                const trimmedOption = option.trim();
                const formFieldValue = props.form.watch(name);
                const isOptionChecked =
                  formFieldValue &&
                  typeof formFieldValue === 'string' &&
                  formFieldValue.includes(trimmedOption);

                return (
                  <Box key={`${option}_${index}`} sx={{ mb: '0.5rem' }}>
                    <InputCheckBox
                      /**
                       * As we have same `name` property to all the options,
                       * adding key forces the options to re-render whenever
                       * the form value change so they are all seeing the
                       * latest form value.
                       */
                      key={Math.random()}
                      label={trimmedOption}
                      name={name}
                      dataCy={name}
                      readOnly={!props.editMode}
                      viewOnly={!props.editMode}
                      checked={isOptionChecked}
                      doNotconsiderFormValue
                      doNotDisplayErrors
                      rules={{
                        validate: (val) => {
                          if (field.required) {
                            const formValue = props.form.getValues()[name];

                            return formValue && typeof formValue === 'string' && formValue.length > 0;
                          }
                          return true;
                        }
                      }}
                      transformAndSave={(checked: boolean, onChange: (value: any) => void) => {
                        let currentValue = props.form.getValues()[name];

                        if (checked) {
                          if (currentValue) {
                            if (typeof currentValue === 'string' && !currentValue.includes(trimmedOption)) {
                              currentValue += ';' + trimmedOption;
                            }
                          } else {
                            currentValue = trimmedOption;
                          }
                        } else {
                          if (
                            currentValue &&
                            typeof currentValue === 'string' &&
                            currentValue.includes(trimmedOption)
                          ) {
                            currentValue = currentValue
                              .split(';')
                              .filter((v) => v !== trimmedOption)
                              .join(';');
                          }
                        }

                        onChange(currentValue);

                        if (props.onBlur) {
                          props.onBlur(field.id, requirementId, requirementResultId);
                        }
                      }}
                      {...props.form}
                    />
                  </Box>
                );
              })}
            </Box>

            {Object.keys(props.form.formState.errors).includes(name) && (
              <Error
                content={
                  props.form.formState.errors[name]
                    ? `* ${field.label} ${
                        props.form.formState.errors[name]?.message ||
                        errorMessages()[String(props.form.formState.errors[name]?.type)]
                      }`
                    : ''
                }
              />
            )}
          </>
        );

      case CustomFieldType.Date:
        return (
          <InputDate
            label={field.label}
            name={name}
            viewFormat="MM-dd-yyyy"
            timezone={props.crbTimezone}
            viewOnly={!props.editMode}
            rules={{ required: { message: 'is required', value: field.required || false } }}
            dataCy={name}
            onBlur={
              props.onBlur ? () => props.onBlur!(field.id, requirementId, requirementResultId) : undefined
            }
            onChange={
              props.onBlur
                ? () => props.onBlur!(field.id, requirementId, requirementResultId)
                : () => onChange(field.id, requirementId, requirementResultId)
            }
            darkLabel
            {...props.form}
          />
        );

      case CustomFieldType.DisplayContent:
        return (
          <Box padding="0" width={isMobile ? '100%' : '580px'}>
            <PreviewCustomFormDisplayContentType
              field={field}
              attachmentsPadding="10px 0 4px 0"
              orgId={props.requirement.bank_id}
              interpolationValues={props.interpolationValues}
            />
          </Box>
        );
      case CustomFieldType.FileDownload:
        return (
          <CustomFormFileDownload
            name={name}
            label={field.label}
            required={field.required}
            form={props.form}
            documentMap={props.documentMap}
            requirementId={requirementId}
            orgId={props.requirement.bank_id}
          />
        );

      case CustomFieldType.Hyperlink:
        return (
          <CustomHyperlink
            url={(field as CustomHyperlinkField).url}
            linkCopy={field.paragraph}
            label={field.label}
          />
        );
    }
  };

  const filteredSortedRequirements = React.useMemo(() => {
    if (!props.results)
      return Object.keys(props.requirement.custom_requirements)
        .filter((sectionId) => {
          return !props.requirement.custom_requirements[sectionId].archived;
        })
        .sort((a, b) => {
          const aa = props.requirement.custom_requirements[a].order;
          const bb = props.requirement.custom_requirements[b].order;

          return aa > bb ? 1 : aa === bb ? 0 : -1;
        });
  }, [props.requirement.custom_requirements]);

  const filteredSortedSections = React.useMemo(() => {
    return props.results?.custom_requirement_result
      .filter((reqResult) => {
        return !props.requirement.custom_requirements[reqResult.requirement_id].archived;
      })
      .sort((a, b) => {
        const aa = props.requirement.custom_requirements[a.requirement_id].order;
        const aSubOrder = a.sub_order || 0;
        const bb = props.requirement.custom_requirements[b.requirement_id].order;
        const bSubOrder = b.sub_order || 0;
        if (aa > bb) return 1;
        if (aa < bb) return -1;
        if (aSubOrder > bSubOrder) return 1;
        if (aSubOrder < bSubOrder) return -1;
        if (aa === bb && aSubOrder === bSubOrder) return 0;

        return aa > bb ? 1 : aa === bb ? 0 : -1;
      });
  }, [props.results?.custom_requirement_result]);

  const lastRepeatedSubSection = React.useMemo(() => {
    const sections: { [sectionId: string]: string } = {};

    filteredSortedSections?.forEach((reqResult) => {
      const requirement = props.requirement.custom_requirements[reqResult.requirement_id];

      if (requirement.custom_section.repeatable) {
        sections[requirement.custom_section.id] = reqResult.id;
      }
    });

    return sections;
  }, [filteredSortedSections]);

  const repeatedSubSections = React.useMemo(() => {
    const sections: { [sectionId: string]: string[] } = {};

    filteredSortedSections?.forEach((reqResult) => {
      const requirement = props.requirement.custom_requirements[reqResult.requirement_id];

      if (requirement.custom_section.repeatable) {
        if (!sections[requirement.custom_section.id]) {
          sections[requirement.custom_section.id] = [];
        }
        sections[requirement.custom_section.id].push(reqResult.id);
      }
    });

    return sections;
  }, [filteredSortedSections]);

  const fieldAnimation = (field: CustomField | CustomSection) => {
    if (animate && field.smart_rule) {
      return '1.5s linear SMART_RULE_ANIM';
    }
  };

  const sectionAnimation = (sectionResultId: string, reqId: string, subOrder?: number) => {
    if (deletingSectionId === sectionResultId && deletingASection) {
      return '0.25s linear DELETE_ANIM';
    }
    if (
      addingSection &&
      newSection?.requirementId === reqId &&
      newSection?.order !== undefined &&
      newSection?.order + 1 === subOrder
    ) {
      setTimeout(() => {
        setAddingSection(false);
      }, 1000);
      return '1s ease-in-out ADD_SECTION_ANIM';
    }
  };

  return (
    <Box padding="0" data-cy="info-form-container" width="100%">
      {props.isLoading && <OverlaySpinner dataCy="overlay-spinner" />}
      {filteredSortedSections?.map((reqResult) => {
        const requirement = props.requirement.custom_requirements[reqResult.requirement_id];
        if (
          shouldDisplayFieldWithSmartRule(
            requirement.custom_section.smart_rule,
            props.form.getValues(),
            props.requirement,
            reqResult.id
          )
        ) {
          return (
            <>
              <Box
                padding="0"
                width="100%"
                data-cy={`form-section-${reqResult.id}`}
                key={reqResult.id}
                sx={{
                  opacity: deletingSectionId === reqResult.id && deletingASection ? 0 : 1,
                  WebkitAnimation: sectionAnimation(
                    reqResult.id,
                    reqResult.requirement_id,
                    reqResult.sub_order
                  ),
                  MozAnimation: sectionAnimation(reqResult.id, reqResult.requirement_id, reqResult.sub_order),
                  OAnimation: sectionAnimation(reqResult.id, reqResult.requirement_id, reqResult.sub_order),
                  animation: sectionAnimation(reqResult.id, reqResult.requirement_id, reqResult.sub_order),
                  animationIterationCount: 1,
                  '@-webkit-keyframes DELETE_ANIM': {
                    from: {
                      opacity: '1'
                    },
                    to: {
                      opacity: '0'
                    }
                  },
                  '@-webkit-keyframes ADD_SECTION_ANIM': {
                    '0%': {
                      background: '#e6ffecff'
                    },
                    '50%': {
                      background: 'rgba(3, 166, 91, 0.15)'
                    },
                    '100%': {
                      background: '#e6ffecff'
                    }
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    WebkitAnimation: fieldAnimation(requirement.custom_section),
                    MozAnimation: fieldAnimation(requirement.custom_section),
                    OAnimation: fieldAnimation(requirement.custom_section),
                    animation: fieldAnimation(requirement.custom_section),
                    animationIterationCount: 1,
                    '@-webkit-keyframes SMART_RULE_ANIM': {
                      from: {
                        backgroundColor: '#e6ffecff'
                      },
                      to: {
                        backgroundColor: 'white'
                      }
                    }
                  }}
                >
                  <Box data-cy={requirement.id} sx={{ display: 'flex', width: '100%', padding: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Header
                        mobileHeader={isMobile}
                        content={
                          requirement.custom_section.repeatable
                            ? interpolateValuesInText(
                                `${requirement.custom_section.label} ${
                                  repeatedSubSections[requirement.custom_section.id].findIndex(
                                    (id) => id === reqResult.id
                                  ) + 1
                                }`,
                                props.interpolationValues
                              )
                            : interpolateValuesInText(
                                requirement.custom_section.label,
                                props.interpolationValues
                              )
                        }
                        type={'h2'}
                      />
                    </Box>

                    {requirement.custom_section.repeatable &&
                      props.editMode &&
                      repeatedSubSections[requirement.custom_section.id].length > 1 && (
                        <Box
                          sx={{
                            cursor: 'pointer',
                            height: '24px',
                            '&:hover .MuiIcon-root': { color: '#3A3C45' }
                          }}
                        >
                          <Tooltip placement="bottom" title="Delete Section">
                            <DeleteIcon
                              style={{ cursor: 'pointer', height: '24px' }}
                              onClick={() => {
                                setDeletingASection(false);
                                setDeletingSectionLabel(`${requirement.custom_section.label}`);
                                setDeletingSectionId(reqResult.id);
                                setConfirmSectionDeleteModalOpen(true);
                              }}
                              data-cy="delete-section-icon"
                            />
                          </Tooltip>
                        </Box>
                      )}
                  </Box>

                  {requirement.custom_section.description && (
                    <ContainerItem padding={'0 8px 8px'}>
                      <Text
                        sx={{ whiteSpace: 'pre-line' }}
                        content={interpolateValuesInText(
                          requirement.custom_section.description,
                          props.interpolationValues
                        )}
                        type="body2"
                      />
                    </ContainerItem>
                  )}
                </Box>
                {requirement.custom_section.fields.map((item) => {
                  if (
                    shouldDisplayFieldWithSmartRule(
                      item.smart_rule,
                      props.form.getValues(),
                      props.requirement,
                      reqResult.id
                    )
                  ) {
                    return (
                      <Box
                        sx={{
                          width: '100%',
                          WebkitAnimation: fieldAnimation(item),
                          MozAnimation: fieldAnimation(item),
                          OAnimation: fieldAnimation(item),
                          animation: fieldAnimation(item),
                          animationIterationCount: 1,
                          '@-webkit-keyframes SMART_RULE_ANIM': {
                            from: {
                              backgroundColor: '#FFFCE6'
                            },
                            to: {
                              backgroundColor: 'white'
                            }
                          }
                        }}
                        key={item.id}
                      >
                        <ContainerItem dataCy="custom-section" width={props.fieldWidth}>
                          <Box
                            sx={{
                              mb: '0.5rem'
                            }}
                          >
                            {getField(
                              item,
                              reqResult.requirement_id,
                              reqResult.id,
                              requirement.custom_section.repeatable
                            )}
                          </Box>

                          {item.helpText && (
                            <Box>
                              <Text
                                content={interpolateValuesInText(item.helpText, props.interpolationValues)}
                                sx={{
                                  color: palette.text.secondary,
                                  fontFamily: 'Lato',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  fontSize: '13px',
                                  lineHeight: '18.2px'
                                }}
                              />
                            </Box>
                          )}
                        </ContainerItem>
                      </Box>
                    );
                  }
                  resetFieldValue(
                    item.id + (requirement.custom_section.repeatable ? `_${reqResult.id}` : '')
                  );
                  return null;
                })}
              </Box>
              {requirement.custom_section.repeatable &&
                props.editMode &&
                lastRepeatedSubSection[requirement.custom_section.id] === reqResult.id && (
                  <Box sx={{ width: '100%', padding: '8px', marginBottom: '40px' }}>
                    <Button
                      width="100%"
                      color="secondary-outlined"
                      label={`+ Add ${requirement.custom_section.label}`}
                      onClick={async () => {
                        if (props.addSection) {
                          await props.addSection(reqResult.id);
                          setAddingSection(true);
                          setNewSection({
                            order: reqResult?.sub_order,
                            requirementId: reqResult.requirement_id
                          });
                        }
                      }}
                    />
                  </Box>
                )}
            </>
          );
        } else {
          return null;
        }
      })}
      {!props.results && (
        <CustomFormGeneratorPreview
          requirement={props.requirement}
          form={props.form}
          fieldWidth={props.fieldWidth}
          filteredSortedRequirements={filteredSortedRequirements}
          isMobile={isMobile}
          fieldAnimation={fieldAnimation}
          resetFieldValue={resetFieldValue}
          getField={getField}
        />
      )}
      <ConfirmSectionDeleteModal
        isOpen={confirmSectionDeleteModalOpen}
        setIsOpen={setConfirmSectionDeleteModalOpen}
        sectionName={deletingSectionLabel}
        onSave={() => {
          setDeletingASection(true);
          setTimeout(async () => {
            if (props.deleteSection && deletingSectionId) {
              await props.deleteSection(deletingSectionId);
              setDeletingSectionId('');
              setDeletingSectionLabel('');
            }
          }, 250);
        }}
      />
    </Box>
  );
};
